/**
 * Service-worker registration — T073, the client half of D-6 / AD-6.
 *
 * The worker itself is `apps/web/public/sw.js`, a static file served verbatim. This module decides
 * whether registering it is appropriate at all, and it is deliberately conservative: the worker is
 * not load-bearing. Without it the whole application works and is merely not installable, so every
 * unusable situation resolves to a quiet `'skipped'` or `'failed'` rather than an error the user has
 * to read. There is no update prompt and no new user-visible workflow (FR-UX-03 asks for an
 * installable application with an offline fallback page, and nothing more).
 *
 * Registration is refused outside a production build. A worker registered from `npm run dev` or from
 * an emulator build outlives the dev server on `localhost`, where it can then shadow a later build —
 * a genuinely confusing local failure that is not worth the convenience of testing the worker in dev.
 *
 * The environment is read through the seam `src/lib/firebase.ts` already owns (`currentEnv`,
 * `emulatorFlagSet`), so there is one environment reader in the client rather than two.
 */
import { currentEnv, emulatorFlagSet } from '../lib/firebase';

/** The static worker and the scope it must control: the whole origin. */
export const SERVICE_WORKER_URL = '/sw.js';
export const SERVICE_WORKER_SCOPE = '/';

/** What happened, from the application's point of view. Never an exception. */
export type ServiceWorkerOutcome = 'registered' | 'skipped' | 'failed';

/** Why a registration was, or was not, attempted. `'eligible'` is the only value that registers. */
export type ServiceWorkerEligibility = 'eligible' | 'unsupported' | 'insecure' | 'disabled';

/** The one method this module calls, declared structurally so a test needs no browser. */
export interface ServiceWorkerContainerLike {
  readonly register: (url: string, options: { readonly scope: string }) => Promise<unknown>;
}

export interface NavigatorLike {
  readonly serviceWorker?: ServiceWorkerContainerLike;
}

export interface SecureContextLike {
  readonly isSecureContext?: boolean;
}

/**
 * Capability first, then transport, then build.
 *
 * The order only matters for the reason reported, never for the decision: everything that is not
 * `'eligible'` skips. It is ordered this way so the reported value describes the most fundamental
 * obstacle — a browser without service workers is not "disabled by configuration".
 */
export function serviceWorkerEligibility(
  nav: NavigatorLike | undefined,
  context: SecureContextLike | undefined,
  env: Readonly<Record<string, unknown>>,
): ServiceWorkerEligibility {
  if (nav === undefined || typeof nav.serviceWorker?.register !== 'function') return 'unsupported';
  if (context?.isSecureContext !== true) return 'insecure';
  if (env['PROD'] !== true || emulatorFlagSet(env)) return 'disabled';
  return 'eligible';
}

/**
 * One attempt, with no memory. A rejected `register` is turned into `'failed'`: the browser has
 * already logged the reason, and there is nothing the person using the application can do about it.
 */
export async function attemptServiceWorkerRegistration(
  nav: NavigatorLike | undefined,
  context: SecureContextLike | undefined,
  env: Readonly<Record<string, unknown>>,
): Promise<ServiceWorkerOutcome> {
  if (serviceWorkerEligibility(nav, context, env) !== 'eligible') return 'skipped';
  try {
    await nav!.serviceWorker!.register(SERVICE_WORKER_URL, { scope: SERVICE_WORKER_SCOPE });
    return 'registered';
  } catch {
    return 'failed';
  }
}

/**
 * The memoising form. React 19 StrictMode runs an effect twice on mount and a navigation may remount
 * the root, so the call site must be safe to repeat; the browser would tolerate a second `register`,
 * but not making the request at all is simpler to reason about and to test.
 */
export function createServiceWorkerRegistrar(
  nav: NavigatorLike | undefined,
  context: SecureContextLike | undefined,
  env: Readonly<Record<string, unknown>>,
): () => Promise<ServiceWorkerOutcome> {
  let inFlight: Promise<ServiceWorkerOutcome> | null = null;
  return () => (inFlight ??= attemptServiceWorkerRegistration(nav, context, env));
}

let browserRegistrar: (() => Promise<ServiceWorkerOutcome>) | null = null;

/**
 * The application's entry point. Resolved lazily so that importing this module — which the unit
 * suite does — reads no global and performs no work.
 */
export function registerServiceWorker(): Promise<ServiceWorkerOutcome> {
  browserRegistrar ??= createServiceWorkerRegistrar(
    typeof navigator === 'undefined' ? undefined : (navigator as NavigatorLike),
    typeof globalThis === 'undefined' ? undefined : (globalThis as SecureContextLike),
    currentEnv(),
  );
  return browserRegistrar();
}
